import React, { useMemo, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Sparkles, Wand2, Loader2, Download, Save, RefreshCcw, ImagePlus, Info, X,
} from 'lucide-react';
import { SPRITES, VARIANTS } from '../mock';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Built-in element vocabulary inspired by the canonical sprite families.
const ELEMENTS = [
  'Water', 'Fire', 'Earth', 'Air', 'Ghost', 'Dream', 'Demon',
  'Punk', 'King', 'Robot', 'Alien', 'Ninja', 'Skeleton', 'Cyber',
];

// Pick one canonical reference per element so the AI matches that style.
function refForElement(element) {
  if (!element) return SPRITES[0];
  const match = SPRITES.find(
    (s) => s.family?.toLowerCase() === element.toLowerCase() && s.variant === 'base'
  );
  return match || SPRITES[0];
}

// Forge-only extra variant — gives the AI creative freedom.
const FORGE_VARIANTS = {
  ...VARIANTS,
  special: { label: 'Special', color: '#ff5ab2' },
};

export default function SpriteForgeDialog({ open, onOpenChange, onSaved }) {
  const [mode, setMode] = useState('guided'); // 'guided' | 'advanced'
  const [element, setElement] = useState('Fire');
  const [theme, setTheme] = useState('');
  const [variant, setVariant] = useState('base');
  const [advancedPrompt, setAdvancedPrompt] = useState('');
  const [editPrompt, setEditPrompt] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { dataUrl, finalPrompt }

  const referenceSprite = useMemo(() => refForElement(element), [element]);

  const reset = () => {
    setResult(null);
    setName('');
    setEditPrompt('');
  };

  const generate = async () => {
    setLoading(true);
    try {
      const body = mode === 'advanced'
        ? { prompt: advancedPrompt, variant, reference_url: referenceSprite.img }
        : { element, theme, variant, reference_url: referenceSprite.img };

      const res = await axios.post(`${API}/sprites/generate`, body, { timeout: 90000 });
      const { data_url, final_prompt } = res.data;
      setResult({ dataUrl: data_url, finalPrompt: final_prompt });
      if (!name) {
        const auto = mode === 'advanced'
          ? 'My Custom Sprite'
          : `${theme ? theme + ' ' : ''}${element} Sprite`.trim();
        setName(auto.replace(/\s+/g, ' '));
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast({ title: 'Generation failed', description: String(detail).slice(0, 140) });
    } finally {
      setLoading(false);
    }
  };

  const applyEdit = async () => {
    if (!result?.dataUrl || !editPrompt.trim()) return;
    setLoading(true);
    try {
      const body = {
        prompt: editPrompt.trim(),
        variant,
        edit_mode: true,
        reference_data_url: result.dataUrl,
      };
      const res = await axios.post(`${API}/sprites/generate`, body, { timeout: 90000 });
      const { data_url, final_prompt } = res.data;
      setResult({ dataUrl: data_url, finalPrompt: final_prompt });
      setEditPrompt('');
      toast({ title: 'Sprite updated!', description: 'Your edits were applied.' });
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast({ title: 'Edit failed', description: String(detail).slice(0, 140) });
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    if (!result?.dataUrl) return;
    const a = document.createElement('a');
    a.href = result.dataUrl;
    a.download = `${(name || 'custom-sprite').replace(/[^a-z0-9-]+/gi, '_')}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const save = () => {
    if (!result?.dataUrl) return;
    onSaved?.({
      name: name || 'Custom Sprite',
      dataUrl: result.dataUrl,
      prompt: result.finalPrompt,
      variant,
      element: mode === 'guided' ? element : '',
      theme: mode === 'guided' ? theme : '',
    });
    toast({ title: 'Saved to My Custom Sprites!', description: name || 'Custom sprite' });
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent
        className="sm:max-w-[640px] w-[calc(100vw-16px)] max-h-[92vh] p-0 border-[3px] border-[#3a1064] rounded-2xl shadow-[0_8px_0_#3a1064] overflow-hidden flex flex-col gap-0"
      >
        {/* Header */}
        <div
          className="p-3 sm:p-5 border-b-[3px] border-[#3a1064] flex items-start justify-between gap-2 shrink-0"
          style={{
            background:
              'radial-gradient(circle at 0% 0%, #ff2bd6 0%, transparent 40%),' +
              'radial-gradient(circle at 100% 0%, #00b8ff 0%, transparent 40%),' +
              'linear-gradient(135deg, #ffd900 0%, #ff9a1c 100%)',
          }}
        >
          <div className="min-w-0 flex-1">
            <DialogHeader className="text-left space-y-1">
              <DialogTitle className="text-[#3a1064] font-black text-lg sm:text-2xl flex items-center gap-2">
                <Wand2 className="h-5 w-5 sm:h-6 sm:w-6 text-[#ff2bd6] shrink-0" /> Sprite Forge
              </DialogTitle>
              <DialogDescription className="text-[#3a1064]/85 font-bold text-xs sm:text-sm">
                Design your own Fortnite-style sprite with AI. Picks up the existing sprite style automatically.
              </DialogDescription>
            </DialogHeader>

            {/* Mode tabs */}
            <div className="mt-2 sm:mt-3 inline-flex rounded-xl bg-white/70 p-1 border-2 border-[#3a1064]">
              {[
                { v: 'guided', label: 'Guided' },
                { v: 'advanced', label: 'Advanced' },
              ].map((m) => (
                <button
                  key={m.v}
                  onClick={() => setMode(m.v)}
                  className={`px-2.5 sm:px-3 h-7 sm:h-8 text-[10px] sm:text-xs font-black uppercase tracking-wide rounded-lg transition-all ${
                    mode === m.v
                      ? 'bg-[#3a1064] text-white shadow-[0_2px_0_#1a0440]'
                      : 'text-[#3a1064] hover:bg-white/60'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <button
            data-testid="forge-close-btn"
            aria-label="Close Sprite Forge"
            onClick={() => onOpenChange(false)}
            className="h-9 w-9 rounded-xl bg-white border-2 border-[#3a1064] text-[#3a1064] shadow-[0_3px_0_#3a1064] hover:bg-yellow-50 active:translate-y-0.5 active:shadow-[0_1px_0_#3a1064] flex items-center justify-center shrink-0"
          >
            <X className="h-4 w-4" strokeWidth={3} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 p-3 sm:p-5 overflow-y-auto bg-white">
          {/* Disclaimer banner */}
          <div
            className="mb-4 rounded-xl border-2 border-[#3a1064] bg-[#fff7d6] p-3 flex items-start gap-2.5 shadow-[0_3px_0_#3a1064]"
          >
            <div className="h-7 w-7 rounded-lg bg-[#ffd900] border-2 border-[#3a1064] flex items-center justify-center shrink-0">
              <Info className="h-4 w-4 text-[#3a1064]" strokeWidth={3} />
            </div>
            <div className="text-[12px] font-bold text-[#3a1064] leading-snug">
              <span className="font-black uppercase tracking-wider text-[10px] block mb-0.5">
                Heads up
              </span>
              AI sprites aren&apos;t always perfect on the first try — but generating is{' '}
              <span className="text-emerald-600 font-black">free</span>, so{' '}
              <span className="text-[#ff2bd6] font-black">re-roll</span> (or use{' '}
              <span className="text-[#9d3dff] font-black">Apply</span> in Advanced mode) until you
              get something close to what you want. These are mock-ups for a real thing — treat
              them as concept art, not final assets.
            </div>
          </div>
          {mode === 'guided' && (
            <div className="space-y-4">
              <div>
                <Label className="text-[#3a1064] font-black mb-1.5 block">Element</Label>
                <div className="flex flex-wrap gap-1.5">
                  {ELEMENTS.map((el) => (
                    <button
                      key={el}
                      onClick={() => setElement(el)}
                      className={`px-3 h-9 rounded-xl text-xs font-black uppercase tracking-wide border-2 transition-all ${
                        element === el
                          ? 'bg-[#ff2bd6] text-white border-[#3a1064] shadow-[0_2px_0_#3a1064]'
                          : 'bg-white text-[#3a1064] border-[#e1d0ff] hover:border-[#3a1064]'
                      }`}
                    >
                      {el}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-[#3a1064] font-black mb-1.5 block">
                  Theme keyword (optional)
                </Label>
                <Input
                  placeholder="e.g. ninja, llama, samurai, pirate, astronaut..."
                  value={theme}
                  onChange={(e) => setTheme(e.target.value)}
                  className="h-11 rounded-xl border-2 border-[#3a1064] font-bold text-[#3a1064]"
                />
              </div>

              <VariantPicker variant={variant} setVariant={setVariant} />

              {/* Reference preview */}
              <div className="rounded-xl border-2 border-dashed border-[#3a1064]/40 p-3 bg-[#fff7d6] flex items-center gap-3">
                <img src={referenceSprite.img} alt="" className="h-12 w-12 object-contain" />
                <div className="text-xs font-bold text-[#3a1064]">
                  <div className="font-black uppercase tracking-wider text-[10px] text-[#3a1064]/70">
                    Style reference
                  </div>
                  {referenceSprite.name} — the AI will match its style exactly.
                </div>
              </div>
            </div>
          )}

          {mode === 'advanced' && (
            <div className="space-y-4">
              <div>
                <Label className="text-[#3a1064] font-black mb-1.5 block">Describe your sprite</Label>
                <Textarea
                  placeholder="A tiny pirate sprite with an eye patch, holding a treasure map, wearing a red bandana..."
                  rows={4}
                  value={advancedPrompt}
                  onChange={(e) => setAdvancedPrompt(e.target.value)}
                  className="rounded-xl border-2 border-[#3a1064] font-medium text-[#3a1064]"
                />
              </div>
              <VariantPicker variant={variant} setVariant={setVariant} />
              <p className="text-xs text-[#3a1064]/70 font-bold">
                The AI will use <span className="text-[#3a1064] font-black">{referenceSprite.name}</span> as the style anchor.
              </p>
            </div>
          )}

          {/* Result preview */}
          {result && (
            <div className="mt-5 rounded-xl border-[3px] border-[#3a1064] shadow-[0_4px_0_#3a1064] overflow-hidden">
              <div className="p-3 bg-[#fff7d6] flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#ff2bd6]" />
                <Input
                  placeholder="Name your sprite..."
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-9 rounded-lg border-2 border-[#3a1064] font-bold text-[#3a1064]"
                />
              </div>
              <div
                className="flex items-center justify-center p-4"
                style={{
                  background:
                    'radial-gradient(circle at 50% 40%, #ffd90055, transparent 70%), #ffffff',
                  minHeight: 280,
                }}
              >
                <img
                  src={result.dataUrl}
                  alt={name || 'Generated sprite'}
                  className="max-h-[260px] object-contain drop-shadow-[0_8px_12px_rgba(58,16,100,0.25)]"
                />
              </div>

              {/* Iterative edit panel (advanced mode only) */}
              {mode === 'advanced' && (
                <div className="p-3 bg-[#fff7d6] border-t-2 border-[#3a1064]/15">
                  <Label className="text-[#3a1064] font-black flex items-center gap-1.5 mb-1.5">
                    <Wand2 className="h-4 w-4 text-[#ff2bd6]" /> Edit / add to this sprite
                  </Label>
                  <div className="flex items-start gap-2">
                    <Textarea
                      placeholder="e.g. give it a wizard hat, add fire eyes, make it blue, hold a sword..."
                      rows={2}
                      value={editPrompt}
                      onChange={(e) => setEditPrompt(e.target.value)}
                      className="flex-1 rounded-xl border-2 border-[#3a1064] font-medium text-[#3a1064]"
                      disabled={loading}
                    />
                    <Button
                      className="h-[60px] rounded-xl border-2 border-[#3a1064] bg-[#9d3dff] hover:bg-[#8230de] text-white font-black shadow-[0_3px_0_#3a1064] shrink-0"
                      onClick={applyEdit}
                      disabled={loading || !editPrompt.trim()}
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <><Wand2 className="h-4 w-4 mr-1" /> Apply</>
                      )}
                    </Button>
                  </div>
                  <p className="mt-1.5 text-[11px] font-bold text-[#3a1064]/70">
                    The AI will modify the current sprite while keeping the style.
                  </p>
                </div>
              )}
            </div>
          )}

          {loading && !result && (
            <div className="mt-5 flex flex-col items-center justify-center gap-3 py-10">
              <Loader2 className="h-12 w-12 text-[#ff2bd6] animate-spin" />
              <div className="font-black text-[#3a1064]">Forging your sprite...</div>
              <div className="text-xs font-bold text-[#3a1064]/70">Usually takes 8–20s</div>
            </div>
          )}
        </div>

        <DialogFooter className="p-3 sm:p-5 bg-white gap-2 flex-wrap border-t-2 border-[#3a1064]/15 shrink-0">
          {result && (
            <Button
              variant="outline"
              className="h-11 rounded-xl border-2 border-[#3a1064] text-[#3a1064] bg-white font-black"
              onClick={generate}
              disabled={loading}
            >
              <RefreshCcw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Re-roll
            </Button>
          )}
          {result && (
            <Button
              variant="outline"
              className="h-11 rounded-xl border-2 border-[#3a1064] text-[#3a1064] bg-white font-black"
              onClick={download}
            >
              <Download className="h-4 w-4 mr-2" /> Download
            </Button>
          )}
          {result ? (
            <Button
              className="h-11 rounded-xl border-2 border-[#3a1064] bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black shadow-[0_3px_0_#3a1064]"
              onClick={save}
            >
              <Save className="h-4 w-4 mr-2" /> Save to my vault
            </Button>
          ) : (
            <Button
              className="h-11 rounded-xl border-2 border-[#3a1064] bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black shadow-[0_3px_0_#3a1064]"
              onClick={generate}
              disabled={loading || (mode === 'advanced' && !advancedPrompt.trim())}
            >
              {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Forging...</>) : (<><Wand2 className="h-4 w-4 mr-2" /> Generate Sprite</>)}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VariantPicker({ variant, setVariant }) {
  return (
    <div>
      <Label className="text-[#3a1064] font-black mb-1.5 block">Variant skin</Label>
      <div className="grid grid-cols-5 gap-1.5">
        {Object.entries(FORGE_VARIANTS).map(([k, v]) => {
          const isSpecial = k === 'special';
          const active = variant === k;
          return (
            <button
              key={k}
              onClick={() => setVariant(k)}
              className={`h-11 rounded-xl border-2 font-black text-xs uppercase tracking-wider transition-all relative overflow-hidden ${
                active ? 'shadow-[0_3px_0_#3a1064] text-[#3a1064]' : 'text-[#3a1064]/70 hover:text-[#3a1064]'
              }`}
              style={{
                background: active
                  ? (isSpecial
                      ? 'linear-gradient(135deg, #ffd900, #ff2bd6, #00b8ff, #2ee6c1)'
                      : v.color)
                  : '#ffffff',
                borderColor: '#3a1064',
              }}
              title={isSpecial ? 'Let AI invent a unique new colour scheme' : ''}
            >
              {isSpecial && active && (
                <span
                  aria-hidden="true"
                  className="absolute inset-0 animate-pulse-slow"
                  style={{
                    background:
                      'linear-gradient(135deg, #ffd900, #ff2bd6, #00b8ff, #2ee6c1, #ffd900)',
                    backgroundSize: '200% 200%',
                  }}
                />
              )}
              <span className="relative inline-flex items-center gap-1">
                {isSpecial && <Sparkles className="h-3 w-3" />} {v.label}
              </span>
            </button>
          );
        })}
      </div>
      {variant === 'special' && (
        <p className="mt-1.5 text-[11px] font-bold text-[#3a1064]/80">
          ✨ AI gets full creative freedom — expect a unique colour scheme every time.
        </p>
      )}
    </div>
  );
}
